"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var batch_1 = require("../batch");
var applyPatchAtPath_1 = require("../Util/applyPatchAtPath");
var assign_1 = require("../Util/assign");
var completeStateUpdate_1 = require("../Util/completeStateUpdate");
var completeStoreUpdate_1 = require("../Util/completeStoreUpdate");
var deepClone_1 = require("../Util/deepClone");
var deepFreeze_1 = require("../Util/deepFreeze");
var findNestedDynamicProxies_1 = require("../Util/findNestedDynamicProxies");
var logActionUpdate_1 = require("../Util/logActionUpdate");
var createObjectProxy_1 = require("./createObjectProxy");
var Store = /** @class */ (function () {
    function Store(state, config, initialValue) {
        if (initialValue === void 0) { initialValue = {}; }
        this.patches = {};
        this.isActioning = false;
        this.config = config;
        this.state = state;
        this.name = this.config.name;
        this.Model = this.config.model;
        // Deep clone initial value into a new instance of the store's model.
        this.state.value[this.name] = (0, deepClone_1.default)(new this.Model(), initialValue);
        // Using the initial value, create a tree of "proxies" for all non-dynamic
        // objects found, excluding arrays. Whenever a "write" operation passes through
        // one of these proxies, we can identify the path of the mutation and
        // patch accordingly.
        this.proxies = (0, createObjectProxy_1.default)(this, this.value);
        // Attach linked store references
        this.attachLinkedReferences();
        // Make value immutable
        (0, deepFreeze_1.default)(this.value);
        // Provide the root proxy to the actions resolver, exposing the actions
        // object to the consumer.
        this.actions = this.createStoreActions(this.proxies.proxy, this.config.actions);
        var initialEmission = this.config.observableArity > 0 ? this.value : void 0;
        this.update$ = new this.config.observable(initialEmission);
        this.internal$ = new this.config.observable(initialEmission);
    }
    Object.defineProperty(Store.prototype, "value", {
        /**
         * Derives the store's value from the state's value. A
         * store's value is actually a computed "slice" of the
         * state's value.
         */
        get: function () {
            return this.state.value[this.name];
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Given a "path", reads the value of a nested property from
     * the store's value. Depending on whether the path points
     * to an array/object proxy, it may return a proxy.
     *
     * @param path          An array of string keys leading from the top of the
     *                      store to the property.
     * @param forceReveal   An optional boolean dictating whether or not to
     *                      return the underyling value regardless of whether
     *                      or not a proxy is present at the path.
     * @param patch         An optional boolean dictating whether to not to
     *                      "patch" the read path.
     * @param getter        An optional getter function if the value at the path
     *                      is a computed property.
     * @return              The value at the path.
     */
    Store.prototype.readPropertyValue = function (path, forceReveal, patch, getter) {
        if (patch === void 0) { patch = false; }
        if (getter === void 0) { getter = null; }
        var value = this.value;
        var proxyNode = this.proxies;
        var patches = this.patches;
        for (var i = 0, key = void 0; i < path.length; i++) {
            key = path[i];
            if (patch) {
                // If the read operation is likely to result in a mutation,
                // apply a patch.
                (0, applyPatchAtPath_1.default)(value, key, patches);
            }
            if (getter && i === path.length - 1) {
                // If a getter has been passed, bind its execution to the
                // proxy, not the underlying object.
                getter = getter.bind(proxyNode.proxy);
            }
            value = value[key];
            // Merge algorithms typically attempt to read all target values
            // for the incoming target to confirm if recursion is needed or not.
            // This *can* result in the reading of undefined properties, as such
            // an escape hatch is needed.
            if (!proxyNode.children)
                return value;
            proxyNode = proxyNode.children[key];
            if (patch)
                patches = patches[key].patches;
        }
        if (!forceReveal && value && typeof value === 'object' && proxyNode) {
            // If a proxy is available at the path, return it
            return proxyNode.proxy;
        }
        // Else, return the actual value (via its getter if present)
        if (getter) {
            return getter();
        }
        return value;
    };
    /**
     * Given a "path", writes to the value of a nested property from
     * the store's value.
     */
    Store.prototype.writePropertyValue = function (path, nextValue) {
        var value = this.value;
        var key;
        var patches = this.patches;
        var proxyNode = this.proxies;
        for (var i = 0; i < path.length; i++) {
            key = path[i];
            (0, applyPatchAtPath_1.default)(value, key, patches);
            // On the penultimate key, break from the loop in order to
            // make the assignment from the parent
            if (i === path.length - 1)
                break;
            proxyNode = proxyNode.children[key];
            value = value[key];
            patches = patches[key].patches;
        }
        var writeKey = key;
        if (nextValue && nextValue._isProxy) {
            nextValue = nextValue.valueOf();
        }
        value[writeKey] = nextValue;
        var isNextValueArray = Array.isArray(nextValue);
        if (nextValue &&
            typeof nextValue === 'object' &&
            !isNextValueArray &&
            proxyNode &&
            proxyNode.children) {
            if (!proxyNode.children[writeKey]) {
                // If a new object or array has been added to the tree,
                // and no proxy exists, create one.
                proxyNode.children[writeKey] = (0, createObjectProxy_1.default)(this, nextValue, path);
            }
            else if (proxyNode.children[writeKey].proxy._isDynamic) {
                // Ensure that patch is marked as an overwrite to prevent
                // unnecessary duck patching.
                patches[writeKey] = { wasOverwritten: true, patches: {} };
                var keyCount = Object.keys(nextValue).length;
                // If overwriting a dynamic object with an empty object, overwrite its proxy
                // with a new proxy so all previous data is flushed.
                if (keyCount === 0) {
                    proxyNode.children[writeKey] = (0, createObjectProxy_1.default)(this, nextValue, path);
                }
            }
            else if (proxyNode.children[writeKey].children &&
                Object.keys(proxyNode.children[writeKey].children).length) {
                var hasNestedDynamicProxies = (0, findNestedDynamicProxies_1.default)(proxyNode.children[writeKey].children);
                if (!hasNestedDynamicProxies)
                    return;
                // A proxied static object (non-array) containing nested proxied dynamic objects
                // is being overwritten with a new object, overwite its proxy with a new
                // proxy so all previous data is flushed
                proxyNode.children[writeKey] = (0, createObjectProxy_1.default)(this, nextValue, path);
            }
        }
    };
    /**
     * For any "links" defined for the store, attaches a reference to that store's
     * initial value, and subscribe to subsequent updates from it.
     */
    Store.prototype.attachLinkedReferences = function () {
        var _this = this;
        var self = this;
        this.config.links.forEach(function (_a) {
            var name = _a.name, map = _a.map;
            var linkedStore = _this.state.stores[name];
            if (process.env.NODE_ENV !== 'production') {
                if (linkedStore === undefined) {
                    throw new TypeError("Cannot link to store \"".concat(name, "\" which has not been created"));
                }
            }
            var skipFirstUpdate = linkedStore.config.observableArity > 0;
            var skip = skipFirstUpdate;
            // Call mapping function once to initialise
            map(linkedStore.value, _this.value);
            var proxiedUpdateAction = _this.createActionProxy(function (latestValue) {
                return map(latestValue, self.proxies.proxy);
            });
            linkedStore.internal$.subscribe(function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                if (skip)
                    return (skip = false);
                proxiedUpdateAction.apply(void 0, args);
                (0, logActionUpdate_1.default)(_this.state.config, _this.name, '_link');
            });
        });
    };
    /**
     * Invokes the consumer-provided `actionsResolver` function,
     * returning a hash of action function with which to trigger
     * mutations to the store's value.
     *
     * For each action, a proxy is created via the `createActionProxy`
     * higher order function, which is what is returned to the consumer.
     */
    Store.prototype.createStoreActions = function (proxy, actionsResolver) {
        var _this = this;
        var actions = {};
        var consumerActions = actionsResolver(proxy, actions);
        return Object.keys(consumerActions).reduce(function (localActions, key) {
            localActions[key] = _this.createActionProxy(consumerActions[key]);
            return localActions;
        }, actions);
    };
    /**
     * A higher order function allowing the store to coordindate lifecyle events
     * (instigation, patch, batch, freeze) between one or more batched actions
     * that are invoked by the consumer.
     */
    Store.prototype.createActionProxy = function (action) {
        var _this = this;
        return function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            var wasGlobalInstigator = false;
            var wasLocalInstigator = false;
            if (!_this.state.isActioning) {
                // The state is not "actioning", so this action is a "global instigator".
                // Create a new proto-state-value by shallow cloning the existing value,
                // and pushing it into the history in a temporarily unfrozen state.
                _this.state.isActioning = true;
                wasGlobalInstigator = true;
                _this.state.pushNextValueToHistory(__assign({}, _this.state.value));
            }
            // If action "batching" is in progress, ensure that the state instance being
            // updated is known by batching logic
            if ((0, batch_1.isBatching)())
                (0, batch_1.registerStateUnderBatching)(_this.state);
            if (!_this.isActioning) {
                // This store is not actioning, so action is a "local instigator".
                // Create a shallow copy of the store's current value, and assign it to
                // the new state value also in a temporarily unfrozen state.
                // Patches may now be applied from the top down.
                _this.isActioning = true;
                wasLocalInstigator = true;
                _this.state.value[_this.name] = (0, assign_1.default)(new _this.Model(), _this.value);
            }
            // If action "batching" is in progress, ensure that each store being
            // updated is known by batching logic
            if ((0, batch_1.isBatching)())
                (0, batch_1.registerStoreUnderBatching)(_this);
            // Invoke the action, thus mutating the store's current value.
            var returnedValue = action.apply(_this.actions, args);
            if (!(0, batch_1.isBatching)()) {
                // If action batching is not in progress, allow the action proxy
                // to perform final cleanup. Otherwise, this is delegated to
                // batching logic.
                if (wasLocalInstigator) {
                    (0, completeStoreUpdate_1.default)(_this);
                }
                if (wasGlobalInstigator) {
                    (0, completeStateUpdate_1.default)(_this.state);
                }
            }
            if (wasGlobalInstigator) {
                (0, logActionUpdate_1.default)(_this.state.config, _this.name, action.name);
            }
            if (!(0, batch_1.isBatching)()) {
                // If action batching is not in progress, emit updates for the updated store,
                // and overarching state, if this action was the instigator at each level.
                // Otherwise, this is delegated to batching logic.
                if (wasLocalInstigator) {
                    _this.update$.next(_this.value);
                }
                if (wasGlobalInstigator) {
                    _this.state.update$.next(_this.state.value);
                }
            }
            else if (!wasGlobalInstigator) {
                (0, logActionUpdate_1.default)(_this.state.config, _this.name, action.name);
            }
            if (returnedValue !== undefined && returnedValue._isProxy) {
                return returnedValue.valueOf();
            }
            return returnedValue;
        };
    };
    return Store;
}());
exports.default = Store;
//# sourceMappingURL=Store.js.map